import { yupResolver } from "@hookform/resolvers/yup"
import { useState } from "react"
import * as yup from "yup"

import CheckBox from "@components/checkbox"
import DialogProvider from "@components/dialog_provider"
import TextInput from "@components/text_input"
import { useCustomerPageContext } from "@contexts/customer"
import { addCouponCodeToCustomerMutation } from "@domain/customer/infos"
import useControlledForm from "@hooks/use_controlled_form"
import { Stack } from "@mui/material"
import { useQueryClient } from "@tanstack/react-query"
import { showErrorToast } from "@utils/toast"

export type AddCouponCodeFormData = {
  code: string
  isYearly: boolean
}

export default function AddCouponCodeDialog({
  isOpen,
  setClose
}: {
  isOpen: boolean
  setClose: () => void
}) {
  const { translate, currentCustomer } = useCustomerPageContext()
  const queryClient = useQueryClient()

  const schema = yup
    .object({
      code: yup.string().required("Code is required"),
      isYearly: yup.boolean().required()
    })
    .required()

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid }
  } = useControlledForm<AddCouponCodeFormData>({
    resolver: yupResolver(schema),
    mode: "all",
    defaultValues: {
      code: "",
      isYearly: false
    }
  })

  const [loading, setLoading] = useState(false)

  function handleClose() {
    if (loading) {
      return
    }

    setClose()
    reset()
  }

  async function onSubmit(data: AddCouponCodeFormData) {
    if (!currentCustomer?._id) {
      return
    }

    setLoading(true)
    const { isSuccess, errorMessage } = await addCouponCodeToCustomerMutation({
      customerId: currentCustomer._id,
      code: data.code,
      isYearly: data.isYearly
    })

    if (!isSuccess) {
      showErrorToast(String(errorMessage))
      setLoading(false)
      return
    }

    queryClient.invalidateQueries({
      queryKey: ["customer-by-id", currentCustomer._id]
    })
    setLoading(false)
    handleClose()
  }

  return (
    <DialogProvider
      title={translate("add_coupon_code")}
      leftButton={{
        text: translate("cancel"),
        color: "secondary",
        onClick: handleClose,
        disabled: loading
      }}
      rightButton={{
        text: translate("save"),
        color: "primary",
        onClick: handleSubmit(onSubmit),
        loading,
        disabled: !isValid
      }}
      isOpen={isOpen}
      setClose={handleClose}
      size="small"
    >
      <Stack gap={1}>
        <TextInput<AddCouponCodeFormData>
          label="Code"
          disabled={loading}
          name="code"
          placeholder={translate("enter")}
          control={control}
        />
        <CheckBox<AddCouponCodeFormData>
          label="Is yearly"
          name="isYearly"
          control={control}
          disabled={loading}
        />
      </Stack>
    </DialogProvider>
  )
}
